import type { Role } from "./types/entities"
import {
  GRANULAR_PERMISSIONS,
  type Permission,
  expandPermissionDependencies,
  isTenantOnlyPermission,
} from "./types/permissions"

// Reviewed suggestions, not authorization aliases. The administrator chooses
// the scope and final permissions when updating the existing role.
const SUGGESTED_REPLACEMENTS: Partial<Record<Permission, Permission[]>> = {
  "users:write": [
    "users:create",
    "users:update",
    "users:update-identity",
    "users:activate",
    "users:deactivate",
    "users:delete",
    "users:import",
    "users:assign-role",
    "users:assign-team",
    "users:adjust-hours",
  ],
  "persons:write": [
    "persons:create",
    "persons:update",
    "persons:activate",
    "persons:deactivate",
    "persons:delete",
    "persons:link",
    "persons:assign-team",
    "persons:adjust-hours",
    "persons:manage-billing",
  ],
  "tasks:read": ["activities:read"],
  "tasks:write": [
    "activities:create",
    "activities:update",
    "activities:delete",
    "activities:assign-users",
    "activities:complete",
  ],
  "tasks:create": ["activities:create"],
  "tasks:assign": ["activities:register-self"],
  "teams:manage": [
    "teams:read",
    "teams:update",
    "teams:delete",
    "users:assign-team",
    "persons:assign-team",
  ],
  "roles:manage": [
    "roles:read",
    "roles:create",
    "roles:update",
    "roles:delete",
  ],
  "settings:write": ["settings:update"],
  "billing:write": ["billing:manage"],
  "billing:read": ["hours:read", "reports:read-summary"],
  "organization-hours:read": ["reports:read-summary"],
  "roles:assign": ["users:assign-role"],
  "teams:assign-members": ["users:assign-team", "persons:assign-team"],
}

const selectable = new Set<string>(GRANULAR_PERMISSIONS)

export const isLegacyAccessRole = (role: Role): boolean =>
  !role.scopePolicy || (role.permissions ?? []).some((p) => !selectable.has(p))

export const getRolePermissionMapping = (
  permissions: readonly string[],
  scopePolicy: "tenant" | "assigned-teams",
) =>
  [...new Set(permissions)].map((source) => {
    const targets = selectable.has(source)
      ? [source as Permission]
      : Object.hasOwn(SUGGESTED_REPLACEMENTS, source)
        ? (SUGGESTED_REPLACEMENTS[source as Permission] ?? [])
        : []
    const expanded = expandPermissionDependencies(targets)
    return {
      source,
      suggested: expanded.filter(
        (p) => scopePolicy === "tenant" || !isTenantOnlyPermission(p),
      ),
      excludedByScope: expanded.filter(
        (p) => scopePolicy === "assigned-teams" && isTenantOnlyPermission(p),
      ),
      needsManualMapping: targets.length === 0,
    }
  })
